import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, DatePickerField, FieldChangeEvent, useFormField } from '@frontend/design-system';
import { addDaysToDate } from '../../../../../utils/date-helpers';

type TrackingIds = {
  dateNavigationTodayButton?: string;
  dateNavigationPrevButton?: string;
  dateNavigationNextButton?: string;
};

type Props = {
  showTodayButton?: boolean;
  selectedDate: string; // should be in 'MM/DD/YYYY' format
  todayDate: string; // should be in 'MM/DD/YYYY' format
  trackingIds: TrackingIds;
  onSelectedDateChange: (value: string) => void;
};

export const CalendarDateSelectorUI = ({
  showTodayButton = true,
  trackingIds,
  selectedDate,
  todayDate,
  onSelectedDateChange,
}: Props) => {
  const { t } = useTranslation('schedule');

  const dateField = useFormField(
    {
      type: 'datePicker',
      required: true,
      minDate: dayjs().subtract(50, 'year').startOf('year').format('YYYY-MM-DD'),
      maxDate: dayjs().add(2, 'year').startOf('year').format('YYYY-MM-DD'),
      value: selectedDate,
    },
    [selectedDate]
  );

  const isTodayButtonDisabled = !dateField.error && selectedDate === todayDate;
  const isNavigationButtonDisabled = !!dateField.error;

  const handleDateNavigation = (num: number) => {
    const newDate = addDaysToDate(selectedDate, num);
    onSelectedDateChange(newDate);
  };

  const handleDateChange = (event?: FieldChangeEvent) => {
    let value = '';

    if (typeof event === 'object') {
      dateField.onChange(event);
      if ('target' in event && event.target && 'value' in event.target) {
        value = String(event.target.value);
      } else if ('value' in event) {
        value = event.value;
      } else {
        value = String(event);
      }
    }

    if (dayjs(value).isValid()) {
      onSelectedDateChange(value);
    }
  };

  return (
    <>
      {showTodayButton && (
        <Button
          variant='secondary'
          size='small'
          disabled={isTodayButtonDisabled}
          trackingId={trackingIds?.dateNavigationTodayButton}
          onClick={() => onSelectedDateChange(todayDate)}
        >
          {t('Today')}
        </Button>
      )}
      <div css={styles.dateSelectorContainer}>
        <Button
          variant='secondary'
          size='small'
          iconName='caret-left'
          hoverLabel={t('Previous Day')}
          css={styles.navigationButton}
          disabled={isNavigationButtonDisabled}
          trackingId={trackingIds?.dateNavigationPrevButton}
          onClick={() => handleDateNavigation(-1)}
        />
        <DatePickerField
          {...dateField}
          hideErrorText
          name='date'
          label=''
          onChange={handleDateChange}
          css={styles.datePickerField(!!dateField.error)}
        />
        <Button
          variant='secondary'
          size='small'
          iconName='caret-right'
          hoverLabel={t('Next Day')}
          css={styles.navigationButton}
          disabled={isNavigationButtonDisabled}
          trackingId={trackingIds?.dateNavigationNextButton}
          onClick={() => handleDateNavigation(1)}
        />
      </div>
    </>
  );
};

const styles = {
  dateSelectorContainer: css`
    display: flex;
    align-items: center;
  `,
  navigationButton: css`
    padding: ${theme.spacing(0.5)};
  `,
  datePickerField: (error: boolean) => css`
    width: 136px;
    height: 32px;
    padding: ${theme.spacing(0, 1)};
    ${error ? `border: 2px solid ${theme.colors.critical30};` : ''};
  `,
};
